import React, { FC, useCallback, useEffect, useState } from "react";
import { Flash, Box, IconButton, Text } from "@primer/react";
import { AlertIcon, XIcon } from "@primer/octicons-react";
import { AdminApiClient } from "@getregistered/greg-editors";
import railsFetch from "../railsFetch";
import { createFileUploadHandler } from "../../controllers/editors/fileUploadHandler";
import { useAwarenessUsers } from "../AwarenessUsers/useAwarenessUsers";
import { PrimerMicrositeEditor } from "./PrimerMicrositeEditor";
import { CollaborativePrimerMicrositeEditorProps } from "./types";

export const CollaborativeMicrositeEditor: FC<CollaborativePrimerMicrositeEditorProps> =
  ({
    channelControl,
    apiBaseUrl,
    fileUploadUrl,
    useRichText,
    markdownSectionsEnabled,
    sponsorshipEnabled,
    agendaBuilderEnabled,
    registrationSite,
    mobileAppEnabled,
    scheduledPagesEnabled,
    registrationFlowType,
    registrationDefaults,
    eventTitle,
    startDate,
    endDate,
    registrationOpens,
    registrationCloses,
    venueName,
    venueCity,
    venueAddress,
    staticMapPreviewUrl,
    checkpoints,
    tracks,
    timezone,
    tieredListTypes,
    eventLogoUrl,
    // GR-1414 — forwarded to MicrositeEventProvider in greg-editors. Omitting
    // any of these here silently hides the Pin Section accordion even when
    // Rails has shipped the flags and the user has microsite:pin_sections.
    pinningEnabled,
    canPinSections,
    canEditPinnedSections,
  }) => {
    const uploadHandler = useCallback(createFileUploadHandler(fileUploadUrl), [
      fileUploadUrl,
    ]);
    const apiClient = new AdminApiClient(apiBaseUrl, railsFetch);
    const awarenessUsers = useAwarenessUsers(channelControl.provider);
    const fullTracks = tracks?.map((t, i) => ({
      ...t,
      inviteUrl: "",
      ticketTypes: [],
      sort: i,
    }));

    // Subscribe to save errors so they surface as a visible Flash banner
    // instead of disappearing into devtools. The previous behavior left users
    // staring at a "Saved → Unsaved" flicker with no explanation, which is
    // exactly what masked the GR-1073 theme persistence bug for so long.
    const [saveError, setSaveError] = useState<string | null>(null);
    useEffect(() => {
      return channelControl.onSaveError((message) => setSaveError(message));
    }, [channelControl]);
    const handleSave = useCallback(() => {
      setSaveError(null);
      channelControl.save();
    }, [channelControl]);
    const handleDismissError = useCallback(() => setSaveError(null), []);

    // GR-1921: surface a dropped cable connection. Without this, a desynced
    // editor looks identical to a healthy one — the user keeps editing an
    // in-memory YDoc whose changes never reach the server or the live preview.
    const [disconnected, setDisconnected] = useState(false);
    useEffect(() => {
      return channelControl.onReadyStateChange((state) =>
        setDisconnected(state === "disconnected")
      );
    }, [channelControl]);

    return (
      <>
        {disconnected && (
          <Box sx={{ position: "sticky", top: 0, zIndex: 1001, p: 2 }}>
            <Flash variant="warning">
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flexShrink: 0, display: "flex", mt: "2px" }}>
                  <AlertIcon size={16} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Text sx={{ fontWeight: "semibold", display: "block" }}>
                    Disconnected — your changes aren’t syncing.
                  </Text>
                  <Text sx={{ fontSize: 1, color: "fg.muted", display: "block", mt: 1 }}>
                    Your edits are safe in this tab. We’re trying to reconnect —
                    don’t reload the page. They’ll sync automatically once the
                    connection is restored.
                  </Text>
                </Box>
              </Box>
            </Flash>
          </Box>
        )}
        {saveError && (
          <Box sx={{ position: "sticky", top: 0, zIndex: 1000, p: 2 }}>
            <Flash variant="danger">
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flexShrink: 0, color: "danger.fg", display: "flex", mt: "2px" }}>
                  <AlertIcon size={16} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Text sx={{ fontWeight: "semibold", display: "block" }}>
                    Save failed — your changes have not been saved.
                  </Text>
                  <Text sx={{ fontSize: 1, color: "fg.muted", display: "block", mt: 1 }}>
                    {saveError}
                  </Text>
                  <Text sx={{ fontSize: 0, color: "fg.muted", display: "block", mt: 1 }}>
                    The editor still has your edits. Try Save again, or contact support if this keeps happening.
                  </Text>
                </Box>
                <IconButton
                  icon={XIcon}
                  variant="invisible"
                  size="small"
                  aria-label="Dismiss save error"
                  onClick={handleDismissError}
                  sx={{ flexShrink: 0 }}
                />
              </Box>
            </Flash>
          </Box>
        )}
        <PrimerMicrositeEditor
        apiClient={apiClient}
        awarenessUsers={awarenessUsers}
        ydoc={channelControl.ydoc}
        currentUser={channelControl.initialAwarenessUser}
        useRichText={useRichText}
        markdownSectionsEnabled={markdownSectionsEnabled}
        sponsorshipEnabled={sponsorshipEnabled}
        agendaBuilderEnabled={agendaBuilderEnabled}
        registrationSite={registrationSite}
        mobileAppEnabled={mobileAppEnabled}
        scheduledPagesEnabled={scheduledPagesEnabled}
        registrationFlowType={registrationFlowType}
        registrationDefaults={registrationDefaults}
        tracks={fullTracks}
        checkpoints={checkpoints}
        timezone={timezone}
        eventTitle={eventTitle}
        startDate={startDate}
        endDate={endDate}
        registrationOpens={registrationOpens}
        registrationCloses={registrationCloses}
        venueName={venueName}
        venueCity={venueCity}
        venueAddress={venueAddress}
        staticMapPreviewUrl={staticMapPreviewUrl}
        tieredListTypes={tieredListTypes}
        eventLogoUrl={eventLogoUrl}
        pinningEnabled={pinningEnabled}
        canPinSections={canPinSections}
        canEditPinnedSections={canEditPinnedSections}
        onSave={handleSave}
        onReset={() => channelControl.reset()}
        onFileUpload={uploadHandler}
      />
      </>
    );
  };
